import React, { useState, useEffect } from 'react';
import { playoffService } from '../../services/playoffService';
import { seasonManagementService } from '../../services/seasonManagementService';
import '../styles/PlayoffManager.css';

const PlayoffManager = ({ selectedSeason }) => {
  const [playoffSettings, setPlayoffSettings] = useState(null);
  const [playoffTeams, setPlayoffTeams] = useState([]);
  const [matchups, setMatchups] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [editingMatchupId, setEditingMatchupId] = useState(null);
  const [matchupScores, setMatchupScores] = useState({});
  const [teamCountChanged, setTeamCountChanged] = useState(false);
  const [pendingTeamCount, setPendingTeamCount] = useState(null);

  useEffect(() => {
    if (selectedSeason?.id) {
      loadPlayoffData();
    }
  }, [selectedSeason]);

  const loadPlayoffData = async () => {
    try {
      setLoading(true);
      setError(null);

      const settings = await playoffService.getPlayoffSettings(selectedSeason.id);
      setPlayoffSettings(settings);
      setPendingTeamCount(settings.playoff_team_count);

      if (settings.id) {
        const teams = await playoffService.getPlayoffTeams(settings.id);
        setPlayoffTeams(teams);

        const matches = await playoffService.getMatchups(settings.id);
        setMatchups(matches);
      }

      const seasonTeams = await seasonManagementService.getTeamsForSeason(selectedSeason.id);
      setAvailableTeams(seasonTeams || []);
    } catch (err) {
      setError(`Failed to load playoff data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePlayoffs = async () => {
    try {
      setLoading(true);
      setError(null);

      const newState = !playoffSettings.playoffs_enabled;
      await playoffService.updatePlayoffSettings(selectedSeason.id, {
        playoffs_enabled: newState
      });

      await loadPlayoffData();
      setSuccess(`Playoffs ${newState ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update playoffs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamCountChange = (newCount) => {
    const parsedCount = parseInt(newCount);
    if (playoffTeams.length > 0 && parsedCount !== playoffSettings.playoff_team_count) {
      setTeamCountChanged(true);
      setPendingTeamCount(parsedCount);
    } else {
      setPendingTeamCount(parsedCount);
    }
  };

  const confirmTeamCountChange = async () => {
    try {
      setLoading(true);
      setError(null);

      await playoffService.validateTeamCount(selectedSeason.id, pendingTeamCount);
      await playoffService.updatePlayoffSettings(selectedSeason.id, {
        playoff_team_count: pendingTeamCount
      });

      setTeamCountChanged(false);
      await loadPlayoffData();
      setSuccess('Team count updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update team count: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFormatChange = async (newFormat) => {
    try {
      setLoading(true);
      setError(null);

      await playoffService.updatePlayoffSettings(selectedSeason.id, {
        playoff_format_type: newFormat
      });

      await loadPlayoffData();
      setSuccess('Format updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update format: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTeam = async (team, seed) => {
    try {
      setLoading(true);
      setError(null);

      const newTeams = [
        ...playoffTeams,
        {
          team_id: team.team_id || team.id,
          seed_number: seed,
          teams: team.teams || team
        }
      ];

      await playoffService.setPlayoffTeams(playoffSettings.id, newTeams);
      await loadPlayoffData();
      setSuccess('Team added to playoffs');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to add team: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeam = async (seed) => {
    try {
      setLoading(true);
      setError(null);

      const newTeams = playoffTeams.filter(t => t.seed_number !== seed);
      await playoffService.setPlayoffTeams(playoffSettings.id, newTeams);
      await loadPlayoffData();
      setSuccess('Team removed from playoffs');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to remove team: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBracket = async () => {
    try {
      setLoading(true);
      setError(null);

      if (playoffTeams.length < playoffSettings.playoff_team_count) {
        setError(`Need ${playoffSettings.playoff_team_count} teams, but only ${playoffTeams.length} are seeded`);
        setLoading(false);
        return;
      }

      await playoffService.generateBracket(
        playoffSettings.id,
        playoffSettings.playoff_format_type,
        playoffSettings.playoff_team_count
      );

      await loadPlayoffData();
      setSuccess('Bracket generated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to generate bracket: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateScore = async (matchupId, team1Score, team2Score, winnerId) => {
    try {
      setLoading(true);
      setError(null);

      await playoffService.updateMatchup(matchupId, {
        team1_score: parseInt(team1Score),
        team2_score: parseInt(team2Score),
        winner_id: winnerId,
        is_completed: winnerId !== null
      });

      await loadPlayoffData();
      setEditingMatchupId(null);
      setMatchupScores({});
      setSuccess('Matchup updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(`Failed to update matchup: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !playoffSettings) {
    return <div className="playoff-manager">Loading playoff data...</div>;
  }

  if (!playoffSettings) {
    return <div className="playoff-manager">Failed to load playoff settings</div>;
  }

  const getUnseededTeams = () => {
    const seededTeamIds = new Set(playoffTeams.map(t => t.team_id || t.teams?.id));
    return availableTeams.filter(t => !seededTeamIds.has(t.team_id || t.teams?.id));
  };

  return (
    <div className="playoff-manager">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="playoff-config-section">
        <h3>Playoff Configuration</h3>

        <div className="config-field">
          <label>Enable Playoffs</label>
          <button
            className={`toggle-btn ${playoffSettings.playoffs_enabled ? 'enabled' : 'disabled'}`}
            onClick={handleTogglePlayoffs}
            disabled={loading}
          >
            {playoffSettings.playoffs_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>

        <div className="config-field">
          <label>Number of Teams</label>
          <div className="team-count-control">
            <select
              value={pendingTeamCount}
              onChange={(e) => handleTeamCountChange(e.target.value)}
              disabled={loading}
            >
              {[2, 4, 6, 8, 12, 16].map(count => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
            {teamCountChanged && (
              <div className="team-count-warning">
                <p>Warning: Changing team count will regenerate the bracket</p>
                <button onClick={confirmTeamCountChange} disabled={loading}>
                  Confirm Change
                </button>
                <button onClick={() => setTeamCountChanged(false)} disabled={loading}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="config-field">
          <label>Playoff Format</label>
          <select
            value={playoffSettings.playoff_format_type}
            onChange={(e) => handleFormatChange(e.target.value)}
            disabled={loading}
          >
            <option value="single_elimination">Single Elimination</option>
            <option value="double_elimination">Double Elimination</option>
            <option value="best_of_1">Best of 1</option>
            <option value="best_of_3">Best of 3</option>
            <option value="best_of_5">Best of 5</option>
            <option value="best_of_7">Best of 7</option>
          </select>
        </div>
      </div>

      <div className="team-seeding-section">
        <h3>Seeding & Team Assignment</h3>

        <button
          className="btn-primary"
          onClick={() => setShowTeamSelector(!showTeamSelector)}
          disabled={loading}
        >
          {showTeamSelector ? 'Hide Team Selector' : 'Add Teams to Playoffs'}
        </button>

        {showTeamSelector && (
          <div className="team-selector-modal">
            <h4>Available Teams</h4>
            <div className="team-list">
              {getUnseededTeams().map((team) => (
                <div key={team.team_id || team.teams?.id} className="team-item">
                  <span className="team-name">{team.teams?.name || team.name}</span>
                  <div className="seed-inputs">
                    {Array.from({ length: playoffSettings.playoff_team_count }, (_, i) => {
                      const seedAlreadyUsed = playoffTeams.some(t => t.seed_number === i + 1);
                      return (
                        <button
                          key={i}
                          onClick={() => handleAddTeam(team, i + 1)}
                          disabled={seedAlreadyUsed || loading}
                          className={seedAlreadyUsed ? 'disabled' : ''}
                          title={seedAlreadyUsed ? 'Seed already used' : `Assign as seed ${i + 1}`}
                        >
                          #{i + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="seeded-teams">
          <h4>Seeded Teams</h4>
          <div className="seeds-grid">
            {playoffTeams.map((team) => (
              <div key={team.seed_number} className="seed-card">
                <div className="seed-number">#{team.seed_number}</div>
                <div className="team-info">
                  {team.teams?.logo_url && (
                    <img src={team.teams.logo_url} alt={team.teams?.name} className="team-logo" />
                  )}
                  <div className="team-name">{team.teams?.name}</div>
                </div>
                <button
                  onClick={() => handleRemoveTeam(team.seed_number)}
                  disabled={loading}
                  className="btn-remove"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bracket-section">
        <h3>Bracket Management</h3>

        <button
          className="btn-primary"
          onClick={handleGenerateBracket}
          disabled={loading || playoffTeams.length < playoffSettings.playoff_team_count}
        >
          Generate Bracket
        </button>

        <div className="matchups-list">
          {matchups.length === 0 ? (
            <p>No bracket generated yet. Add teams and generate bracket to begin.</p>
          ) : (
            <div>
              {Array.from({ length: Math.max(...matchups.map(m => m.round_number || 0)) }).map((_, roundIdx) => {
                const roundNumber = roundIdx + 1;
                const roundMatchups = matchups.filter(m => m.round_number === roundNumber);

                return (
                  <div key={roundNumber} className="round">
                    <h4>Round {roundNumber}</h4>
                    <div className="matchups-in-round">
                      {roundMatchups.map((matchup) => (
                        <div key={matchup.id} className="matchup-card">
                          <div className="matchup-teams">
                            <div className={`team ${matchup.winner_id === matchup.team1_id ? 'winner' : ''}`}>
                              {matchup.team1 ? (
                                <>
                                  <span className="seed">({matchup.team1_seed})</span>
                                  <span className="name">{matchup.team1?.name}</span>
                                </>
                              ) : (
                                <span className="tbd">TBD</span>
                              )}
                              {matchup.team1_score !== null && (
                                <span className="score">{matchup.team1_score}</span>
                              )}
                            </div>
                            <div className="vs">vs</div>
                            <div className={`team ${matchup.winner_id === matchup.team2_id ? 'winner' : ''}`}>
                              {matchup.team2 ? (
                                <>
                                  <span className="seed">({matchup.team2_seed})</span>
                                  <span className="name">{matchup.team2?.name}</span>
                                </>
                              ) : (
                                <span className="tbd">TBD</span>
                              )}
                              {matchup.team2_score !== null && (
                                <span className="score">{matchup.team2_score}</span>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => setEditingMatchupId(editingMatchupId === matchup.id ? null : matchup.id)}
                            disabled={!matchup.team1 || !matchup.team2 || loading}
                            className="btn-edit-score"
                          >
                            {editingMatchupId === matchup.id ? 'Close' : 'Edit Score'}
                          </button>

                          {editingMatchupId === matchup.id && matchup.team1 && matchup.team2 && (
                            <div className="score-editor">
                              <div className="score-input">
                                <label>{matchup.team1?.name}</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={matchupScores[`${matchup.id}_t1`] ?? matchup.team1_score ?? ''}
                                  onChange={(e) => setMatchupScores({
                                    ...matchupScores,
                                    [`${matchup.id}_t1`]: e.target.value
                                  })}
                                />
                              </div>
                              <div className="score-input">
                                <label>{matchup.team2?.name}</label>
                                <input
                                  type="number"
                                  min="0"
                                  value={matchupScores[`${matchup.id}_t2`] ?? matchup.team2_score ?? ''}
                                  onChange={(e) => setMatchupScores({
                                    ...matchupScores,
                                    [`${matchup.id}_t2`]: e.target.value
                                  })}
                                />
                              </div>
                              <div className="winner-select">
                                <label>Winner</label>
                                <select defaultValue={matchup.winner_id || ''} onChange={(e) => {
                                  setMatchupScores({
                                    ...matchupScores,
                                    [`${matchup.id}_winner`]: e.target.value
                                  });
                                }}>
                                  <option value="">Select Winner</option>
                                  <option value={matchup.team1_id}>{matchup.team1?.name}</option>
                                  <option value={matchup.team2_id}>{matchup.team2?.name}</option>
                                </select>
                              </div>
                              <button
                                onClick={() => handleUpdateScore(
                                  matchup.id,
                                  matchupScores[`${matchup.id}_t1`] ?? matchup.team1_score ?? 0,
                                  matchupScores[`${matchup.id}_t2`] ?? matchup.team2_score ?? 0,
                                  matchupScores[`${matchup.id}_winner`]
                                )}
                                disabled={loading}
                                className="btn-save"
                              >
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayoffManager;
